import {
  formatStructuredValue,
  isStructuredValue,
} from '@aldebaran/common/util/structuredValue';
import * as Sentry from '@sentry/react';
import difference from 'lodash/difference';
import partition from 'lodash/partition';
import pull from 'lodash/pull';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import uniq from 'lodash/uniq';
import remove from 'lodash/remove';
import sortBy from 'lodash/sortBy';
import { v4 as uuid } from 'uuid';
import { createAction, createReducer } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';
import { QuestionnaireVariant } from '@aldebaran/prisma';

import {
  checkCondition,
  evaluate,
  sortQuestions,
  standardValue,
  valueRegex,
} from '../util/graph';
import { filterOnboardingQuestions } from '../util/questionnaire';
import { Page } from '../util/sections';
import {
  FormulaStore,
  GraphState,
  Mode,
  modes,
  QuestionnaireConfig,
  RecordedResponses,
  RootState,
} from '../types/graph';
import {
  Assertion,
  Formula,
  InsertableQuestion,
  UserValue,
  ViewableQuestion,
  ViewableResponse,
} from '../types/global';
import { Question } from '../types/kb';
import { getEffectiveProfessional } from '../util/professional';
import { Patient, Practice, Professional } from '@aldebaran/prisma';
import { ProfessionalWithPractice } from '@aldebaran/common/types';
import { initialQuestionIds } from '@aldebaran/common/constants/kb';
import { getDefaultConfig } from '@aldebaran/common/qVariants';
import { addToSet, removeFromSet } from '../util/set';
import { fromPairs } from 'lodash';

interface QueueModifications {
  toInsert: InsertableQuestion[];
  toDelete: string[];
}

export const CONSULTATION_TIME_KNOWN = 'CONSULTATION_TIME_KNOWN';

const options = {
  COMPACT: false,
  ALERTES: false,
  CONSULTATION_TIME_KNOWN: false,
  SINGLE_MOTIVE: false,
  LIMITED_MOTIVES: false,
  LIMITED_PREVENTION: false,
  DISPLAY_FULL_QUEUE: localStorage.getItem('DISPLAY_FULL_QUEUE') === 'true',
  ASSISTED_BY_PRO: false,
  // Mirrors the controlling professional's patientDocumentsEnabled flag so the KB
  // can branch FILE_UPLOAD questions on it. Set from the professional in
  // updateProfessional + setQuestionnaireConfig.
  PATIENT_DOCUMENTS_ENABLED: false,
};

export const initialState: RootState = {
  graph: {
    values: {},
    assertions: [],
    assertionsByQuestion: {},
    assertionsByResponse: {},
    currentPage: 'INTRODUCTION',
    options,
    personalizationFlags: {},
    variant: 'DEFAULT',
    onboardingFlow: [],
    currentStepIndex: 0,
    experimentFlags: {},
  },
  questionnaire: {
    currentQuestionIndex: 0,
    maxPage: 0,
    queue: Object.values(initialQuestionIds),
    pageAssignments: {},
    sectionAssignments: {},
    insertedBy: {},
    insertedByResponses: {},
    sectionQueues: {},
    isQueueReady: true,
    totalQueue: [],
    validQuestions: [],
    surveyRatingSent: false,
    checklistItems: {},
    areQuestionsUpdating: false,
  },
  kb: {
    savedRawQuestions: [],
    questions: {},
    responses: {},
    formulas: {},
    formulasLoaded: false,
  },
  consultation: {},
  userResponses: {},
  sessionId: uuid(),
  questionsUpdatedCount: 0,
  version: {
    current: undefined,
    query: undefined,
    env: undefined,
    api: undefined,
  },
  queryVariant: undefined,
  previousQueryVariant: undefined,
  searchParams: {},
  previousSearchParams: {},
  isFullReset: false,
  invitationFallback: false,
  patientSegment: null,
};

export const recordResponses =
  createAction<RecordedResponses>('RECORD_RESPONSES');
export const setCurrentQuestionIndex = createAction<number>(
  'SET_CURRENT_QUESTION_INDEX',
);
export const navigateToNextQuestion = createAction<void>(
  'NAVIGATE_TO_NEXT_QUESTION',
);
export const navigateToPreviousQuestion = createAction<void>(
  'NAVIGATE_TO_PREVIOUS_QUESTION',
);
export const nextPage = createAction<void>('NEXT_PAGE');
export const previousPage = createAction<void>('PREVIOUS_PAGE');
export const changePage = createAction<Page>('CHANGE_PAGE');
export const insertQuestions =
  createAction<InsertableQuestion[]>('INSERT_QUESTIONS');
export const updateSavedRawQuestions = createAction<Question[]>(
  'UPDATE_SAVED_RAW_QUESTIONS',
);
export type UpdateQuestionsPayload = {
  questions: ViewableQuestion[];
  coveredIds: ViewableQuestion['extId'][];
};
export const updateQuestions =
  createAction<UpdateQuestionsPayload>('UPDATE_QUESTIONS');
export const updateCache = createAction<ViewableQuestion[]>('UPDATE_CACHE');
export const updateFormulas = createAction<Formula[]>('UPDATE_FORMULAS');

export const updatePatient = createAction<Patient>('UPDATE_PATIENT');
export const clearPatient = createAction<Patient>('CLEAR_PATIENT');
export const updatePractice = createAction<
  Practice & { users: Professional[] }
>('UPDATE_PRACTICE');
export const clearPractice = createAction('CLEAR_PRACTICE');
export const updateProfessional = createAction<ProfessionalWithPractice>(
  'UPDATE_PROFESSIONAL',
);
export const clearProfessional = createAction('CLEAR_PROFESSIONAL');
export const updateReferrer = createAction<string>('UPDATE_REFERRAL_SOURCE');
export const updateQuestionnaireId = createAction<string | undefined>(
  'UPDATE_QUESTIONNAIRE_ID',
);
export const updateQuestionnaireRevision = createAction<number | undefined>(
  'UPDATE_QUESTIONNAIRE_REVISION',
);
export const setQuestionnaireConflict = createAction(
  'SET_QUESTIONNAIRE_CONFLICT',
);
export const updateInvitationId = createAction<string | null>(
  'UPDATE_INVITATION_ID',
);
export const updateInvitationSlug = createAction<string | null>(
  'UPDATE_INVITATION_SLUG',
);
export const updateConsultationDate = createAction<string>(
  'UPDATE_CONSULTATION_DATE',
);
export const updateConsultationVariant = createAction<QuestionnaireVariant>(
  'UPDATE_CONSULTATION_VARIANT',
);
export const setMode = createAction<Mode>('SET_MODE');
export const setInvitationFallback = createAction<boolean>(
  'SET_INVITATION_FALLBACK',
);
export const setPatientSegment = createAction<string | null>(
  'SET_PATIENT_SEGMENT',
);
export const setNoAppointmentMode = createAction<boolean>('SET_NO_APPOINTMENT');
export const acceptCGU = createAction('ACCEPT_CGU');
export const recordResearchConsent = createAction<boolean>(
  'RECORD_RESAERCH_CONSENT',
);

export const addToTotalQueue = createAction<{
  currentPage: Page;
  queue: string[];
}>('ADD_TO_TOTAL_QUEUE');
export const setConsultationTimeKnown = createAction<boolean>(
  'CONSULTATION_TIME_KNOWN',
);
export const setDisplayFullQueue = createAction<boolean>('DISPLAY_FULL_QUEUE');
export const setQuestionsByQuestionMode = createAction<boolean>(
  'QUESTIONS_BY_QUESTION_MODE',
);
export const setAssistedByPro = createAction<boolean>('SET_ASSISTED_BY_PRO');
export const setOnboardingFlow = createAction<string[]>('SET_ONBOARDING_FLOW');
export const setCurrentStepIndex = createAction<number>(
  'SET_CURRENT_STEP_INDEX',
);
export const setSurveyRatingSent = createAction<boolean>(
  'SET_SURVEY_RATING_SENT',
);

export const setIsFrequentFlyer = createAction<boolean>(
  'SET_IS_FREQUENT_FLYER',
);

export const setInterlocuter = createAction<'SELF' | 'OTHER'>(
  'SET_INTERLOCUTER',
);

export const setQuestionnaireConfig = createAction<QuestionnaireConfig>(
  'SET_QUESTIONNAIRE_CONFIG',
);

export const setQueryVariant = createAction<string>('SET_QUERY_VARIANT');
export const setSearchParams =
  createAction<Record<string, string>>('SET_SEARCH_PARAMS');

export const setFullReset = createAction<boolean>('SET_SHIFT_RESET');
export const resetAreQuestionsUpdating = createAction<void>(
  'RESET_ARE_QUESTIONS_UPDATING',
);

export const setExperimentFlags = createAction<Record<string, string>>(
  'SET_EXPERIMENT_FLAGS',
);

const getQueueModifications = (
  state: RootState,
  responseId: ViewableResponse['extId'],
  removalMode = false,
) => {
  const { insertsQuestions, deletesQuestions } = state.kb.responses[responseId];

  const queueModifications =
    removalMode === true
      ? {
          toInsert: deletesQuestions,
          toDelete: insertsQuestions.map((q) => q.extId),
        }
      : {
          toInsert: insertsQuestions,
          toDelete: deletesQuestions.map((q) => q.extId),
        };

  queueModifications.toInsert.forEach((q) => {
    state.questionnaire.insertedByResponses[q.extId] = addToSet(
      state.questionnaire.insertedByResponses[q.extId],
      responseId,
    );
  });

  queueModifications.toDelete.forEach((q) => {
    state.questionnaire.insertedByResponses[q] = removeFromSet(
      state.questionnaire.insertedByResponses[q],
      responseId,
    );
  });

  return queueModifications;
};

const unassert = (graph: GraphState, assertionStrings: string[]) => {
  const toRemove = assertionStrings.map((a: string) =>
    a.split('-<EVALUE>->')[0].replace(valueRegex, standardValue),
  );
  pull(graph.assertions, ...toRemove);

  Object.keys(graph.assertionsByQuestion).forEach((questionId) => {
    graph.assertionsByQuestion[questionId] = graph.assertionsByQuestion[
      questionId
    ].filter((assertion) => !toRemove.includes(assertion));

    if (graph.assertionsByQuestion[questionId].length === 0) {
      delete graph.assertionsByQuestion[questionId];
    }
  });

  Object.keys(graph.assertionsByResponse).forEach((responseId) => {
    if (graph.assertionsByResponse[responseId]) {
      graph.assertionsByResponse[responseId] = graph.assertionsByResponse[
        responseId
      ].filter((assertion) => !toRemove.includes(assertion));

      if (graph.assertionsByResponse[responseId].length === 0) {
        delete graph.assertionsByResponse[responseId];
      }
    }
  });

  graph.values = omit(graph.values, ...toRemove);
};

const assert = (
  graph: GraphState,
  formulas: FormulaStore,
  assertionStrings: string[],
  userValue: UserValue,
  questionId?: string,
  responseId?: string,
) => {
  assertionStrings.forEach((a: string) => {
    const [assertion, clause] = a.split('-<EVALUE>->');

    let value: UserValue | null | undefined;
    const cleaned = assertion.replace(valueRegex, (_, match, unit) => {
      if (clause) {
        const evaluatedValue = evaluate(graph.values, formulas, clause);
        if (evaluatedValue !== undefined)
          value = { value: evaluate(graph.values, formulas, clause) };
        else value = null;
      } else if (['#?', '?', '*'].includes(match)) {
        value = userValue;
      } else {
        value = { value: !isNaN(match) ? Number(match) : match };
      }

      if (value && !value.unit && unit) value.unit = { plural: unit };

      return standardValue;
    });
    if (value !== null) {
      graph.assertions.push(cleaned);

      if (questionId) {
        if (!graph.assertionsByQuestion[questionId]) {
          graph.assertionsByQuestion[questionId] = [];
        }
        graph.assertionsByQuestion[questionId].push(cleaned);
      }

      if (responseId) {
        if (!graph.assertionsByResponse[responseId]) {
          graph.assertionsByResponse[responseId] = [];
        }
        graph.assertionsByResponse[responseId].push(cleaned);
      }

      if (value !== undefined) graph.values[cleaned] = value;
    }
  });
};

const getValidAssertions = (
  graph: GraphState,
  formulas: FormulaStore,
  assertions: Assertion[],
) => {
  const validAssertions = assertions
    .map((assertion) => assertion.value)
    .map((assertion) => {
      const [assertionString, conditionString] =
        assertion.split('-<CONDITION>->');
      if (
        conditionString &&
        !checkCondition(graph, formulas, conditionString)
      ) {
        return '';
      }
      return assertionString;
    })
    .filter((a) => a);

  const [evaluatedAssertions, standardAssertions] = partition(
    validAssertions,
    (assertion) => assertion.includes('<EVALUE>'),
  );

  return [...standardAssertions, ...evaluatedAssertions];
};

const removeResponse = (state: RootState, responseId: string) => {
  const response = state.kb.responses[responseId];
  const responseAssertions = state.graph.assertionsByResponse[responseId] || [];
  delete state.graph.assertionsByResponse[responseId];
  const otherAssertions = Object.values(
    state.graph.assertionsByResponse,
  ).flat();
  const assertionsToRemove = responseAssertions.filter(
    (assertion) => !otherAssertions.includes(assertion),
  );

  unassert(state.graph, assertionsToRemove);

  // unassert(
  //   state.graph,
  //   getValidAssertions(state.graph, state.kb.formulas, response.assertions),
  // );

  if (state.userResponses[response.question])
    delete state.userResponses[response.question][responseId];

  const { toInsert, toDelete } = getQueueModifications(state, responseId, true);

  response.insertsQuestions.forEach((q) => {
    if (state.userResponses[q.extId])
      Object.keys(
        pickBy(state.userResponses[q.extId], (r) => r !== false && r !== ''),
      ).forEach((extId) => {
        const res = removeResponse(state, extId);
        toInsert.push(...res.toInsert);
        toDelete.push(...res.toDelete);
      });
  });

  if (response.checklistItems.length > 0) {
    delete state.questionnaire.checklistItems[response.extId];
  }

  return {
    toInsert,
    toDelete,
  };
};

const addResponse = (
  state: RootState,
  responseId: string,
  value: UserValue,
) => {
  const response = state.kb.responses[responseId];
  const remainingAssertions = [...response.assertions];

  let assertions = getValidAssertions(
    state.graph,
    state.kb.formulas,
    response.assertions,
  );
  unassert(state.graph, assertions);

  assertions = getValidAssertions(
    state.graph,
    state.kb.formulas,
    response.assertions,
  );

  // The graph holds scalars only: a structured answer is asserted as its display
  // string (`[Valeur: #?]` → "Dr Michel MICHU à Paris"), while userResponses
  // keeps the object.
  const assertedValue: UserValue = isStructuredValue(value.value)
    ? { ...value, value: formatStructuredValue(value.value) }
    : value;

  while (assertions.length > 0) {
    remove(remainingAssertions, (a) =>
      assertions.includes(a.value.split('-<CONDITION>->')[0]),
    );

    assert(
      state.graph,
      state.kb.formulas,
      assertions,
      assertedValue,
      response.question,
      responseId,
    );

    assertions = getValidAssertions(
      state.graph,
      state.kb.formulas,
      remainingAssertions,
    );
  }

  if (state.userResponses[response.question])
    state.userResponses[response.question][responseId] = value.value;
  else state.userResponses[response.question] = { [responseId]: value.value };

  if (response.checklistItems.length > 0) {
    state.questionnaire.checklistItems[response.extId] =
      response.checklistItems.map((item) => ({
        value: item.value,
        family: item.family,
      }));
  }

  return getQueueModifications(state, responseId, false);
};

const updateQueue = (
  state: RootState,
  { toInsert, toDelete }: QueueModifications,
  currentQuestion?: ViewableQuestion['extId'],
  currentPage?: Page,
  currentSection?: string,
) => {
  toInsert.forEach((q) => {
    if (currentQuestion)
      state.questionnaire.insertedBy[q.extId] = currentQuestion;
    if (currentPage) state.questionnaire.pageAssignments[q.extId] = currentPage;
    if (currentSection)
      state.questionnaire.sectionAssignments[q.extId] = currentSection;
  });
  return uniq(
    state.questionnaire.queue
      .filter(
        (extId) =>
          !toDelete.includes(extId) ||
          state.questionnaire.insertedByResponses[extId]?.length,
      )
      .concat(toInsert.map((q) => q.extId))
      .filter((extId) => extId),
  );
};

const applyQuestionnaireConfig = (
  state: RootState,
  config: QuestionnaireConfig,
) => {
  const variant = config.slug;
  const effectiveProfessional = getEffectiveProfessional(state.consultation);
  const personalizationFlags =
    effectiveProfessional?.questionnaireOptions?.[variant] ??
    getDefaultConfig(variant);

  state.graph = {
    ...initialState.graph,
    mode: state.graph.mode,
    experimentFlags: state.graph.experimentFlags,
    assertions: config.initialAssertions ?? [],
    options: {
      ...options,
      ...fromPairs(config.options.map((o: string) => [o, true])),
      // Professional-derived (not part of the variant's KB options), so set it
      // explicitly here since this rebuilds graph from initialState.
      PATIENT_DOCUMENTS_ENABLED:
        effectiveProfessional?.patientDocumentsEnabled ?? false,
    },
    personalizationFlags,
    variant,
  };
  state.questionnaire = {
    ...initialState.questionnaire,
    queue: [...Object.values(initialQuestionIds), ...config.initialQuestions],
    config,
  };
  state.kb = initialState.kb;
};

const updateQuestionInCache = (state: RootState, q: ViewableQuestion) => {
  state.kb.questions[q.extId] = q;
  q.responses.forEach((r) => {
    state.kb.responses[r.extId] = r;
  });
  return q.extId;
};

export default createReducer(initialState, (builder) => {
  builder
    .addCase(recordResponses, (state, action) => {
      state.questionnaire.currentQuestion = action.payload.questionId;

      const [toAdd, toRemove] = partition(
        action.payload.responses,
        (r) => r.value.value !== false && r.value.value !== '',
      );

      const queueModifications: QueueModifications = {
        toInsert: [],
        toDelete: [],
      };

      toRemove.forEach((r) => {
        const result = removeResponse(state, r.extId);
        queueModifications.toInsert.push(...result.toInsert);
        queueModifications.toDelete.push(...result.toDelete);
      });

      toAdd.forEach((r) => {
        const result = addResponse(state, r.extId, r.value);
        queueModifications.toInsert.push(...result.toInsert);
        queueModifications.toDelete.push(...result.toDelete);
      });

      state.graph.assertions = uniq(state.graph.assertions);

      state.questionnaire.queue = updateQueue(
        state,
        queueModifications,
        action.payload.questionId,
        action.payload.currentPage,
        action.payload.currentSection,
      );

      // Mark as updating if queue was modified (questions inserted or deleted)
      if (
        queueModifications.toInsert.length > 0 ||
        queueModifications.toDelete.length > 0
      ) {
        state.questionnaire.areQuestionsUpdating = true;
      }

      // DEBUG empty motives
      if (state.graph.currentPage === 'COMPLAINTS') {
        Sentry.addBreadcrumb({
          category: 'response',
          message: `Response recorded: ${action.payload.questionId}`,
          level: 'info',
          data: {
            questionId: action.payload.questionId,
            currentQuestionIndex: state.questionnaire.currentQuestionIndex,
            queueModifications: {
              inserted: queueModifications.toInsert.map((q) => q.extId),
              deleted: queueModifications.toDelete,
            },
          },
        });
      }
    })
    .addCase(setCurrentQuestionIndex, (state, action) => {
      state.questionnaire.currentQuestionIndex = action.payload;
    })
    .addCase(navigateToNextQuestion, (state) => {
      const currentPageSections =
        state.questionnaire.config?.pages[state.graph.currentPage]?.sections ||
        [];
      const activeQueue = currentPageSections.flatMap(
        (section) => state.questionnaire.sectionQueues[section] || [],
      );
      const displayQueue = filterOnboardingQuestions(
        activeQueue,
        state.graph.currentPage,
      );

      if (state.questionnaire.currentQuestionIndex < displayQueue.length - 1) {
        state.questionnaire.currentQuestionIndex += 1;
      }
    })
    .addCase(navigateToPreviousQuestion, (state) => {
      if (state.questionnaire.currentQuestionIndex > 0) {
        state.questionnaire.currentQuestionIndex -= 1;
      }
    })
    .addCase(changePage, (state, action) => {
      const pages = Object.keys(state.questionnaire.config?.pages ?? {});
      const newPage = action.payload;
      const currentPageIndex = pages.indexOf(state.graph.currentPage);
      const newPageIndex = pages.indexOf(newPage);

      const isNavigatingForward = newPageIndex > currentPageIndex;
      const isNavigatingBackward = newPageIndex < currentPageIndex;

      state.graph.currentPage = newPage;

      if (newPageIndex > state.questionnaire.maxPage) {
        state.questionnaire.maxPage = newPageIndex;
      }

      const questionsForCurrentPage =
        state.questionnaire.totalQueue.find(
          (pageQueue) => pageQueue[newPage],
        )?.[newPage] || [];

      // special case for consultation page
      const displayQueue =
        newPage === 'CONSULTATION'
          ? questionsForCurrentPage.slice(2)
          : questionsForCurrentPage;

      if (isNavigatingForward) {
        // navigate forward : start from the first question
        state.questionnaire.currentQuestionIndex = 0;
        // Mark as not ready - updateQuestions will rebuild/update sections for new page.
        state.questionnaire.isQueueReady = false;
      } else if (isNavigatingBackward) {
        // navigate backward : start from the last question
        state.questionnaire.currentQuestionIndex = Math.max(
          0,
          displayQueue.length - 1,
        );
        // For backward navigation, we already have the questions in totalQueue
        state.questionnaire.isQueueReady = true;
      }
    })
    .addCase(nextPage, (state) => {
      const pages = Object.keys(state.questionnaire.config?.pages ?? {});
      const index = pages.indexOf(state.graph.currentPage);
      if (index < pages.length - 1) {
        state.graph.currentPage = pages[index + 1];
        if (index + 1 > state.questionnaire.maxPage)
          state.questionnaire.maxPage = index + 1;
      }
    })
    .addCase(previousPage, (state) => {
      const pages = Object.keys(state.questionnaire.config?.pages ?? {});
      const index = pages.indexOf(state.graph.currentPage);
      if (index > 0) state.graph.currentPage = pages[index - 1];
    })
    .addCase(insertQuestions, (state, action) => {
      state.questionnaire.queue = updateQueue(state, {
        toInsert: [...action.payload],
        toDelete: [],
      });
    })
    .addCase(updateCache, (state, action) => {
      action.payload.forEach((q) => updateQuestionInCache(state, q));
    })
    .addCase(updateSavedRawQuestions, (state, action) => {
      action.payload.forEach((q) => {
        const existingIndex = state.kb.savedRawQuestions.findIndex(
          (cached) => cached.extId === q.extId,
        );
        const questionExists = existingIndex >= 0;

        if (questionExists) {
          state.kb.savedRawQuestions[existingIndex] = q;
        } else {
          state.kb.savedRawQuestions.push(q);
        }
      });
    })
    .addCase(updateQuestions, (state, action) => {
      const { questions: payload, coveredIds } = action.payload;
      const covered = new Set(coveredIds);
      const next = payload.map((q) => updateQuestionInCache(state, q));

      const removedQuestions = difference(
        state.questionnaire.validQuestions,
        next,
      ).filter((extId) => covered.has(extId));

      const queueModifications: QueueModifications = {
        toInsert: [],
        toDelete: [],
      };

      removedQuestions.forEach(
        (extId) =>
          state.kb.questions[extId]?.responses?.forEach((r) => {
            const result = removeResponse(state, r.extId);
            queueModifications.toInsert.push(...result.toInsert);
            queueModifications.toDelete.push(...result.toDelete);
          }),
      );

      const nextQueue = updateQueue(state, queueModifications);

      // ALDO-2698: avoid queue identity churn feeding the KB-sync effect.
      if (
        nextQueue.length !== state.questionnaire.queue.length ||
        !nextQueue.every(
          (extId, index) => extId === state.questionnaire.queue[index],
        )
      ) {
        state.questionnaire.queue = nextQueue;
      }

      const stillValidUncovered = state.questionnaire.validQuestions.filter(
        (extId) =>
          !covered.has(extId) && state.questionnaire.queue.includes(extId),
      );
      state.questionnaire.validQuestions = sortBy(
        uniq([...next, ...stillValidUncovered]),
        (extId) => state.questionnaire.queue.indexOf(extId),
      );

      // Get the current page's sections in order (for oldActiveQueue calculation)
      let currentPageSections: string[] = [];
      try {
        currentPageSections =
          state.questionnaire.config!.pages[state.graph.currentPage].sections;
      } catch (_e) {
        //do nothing
      }

      // Get ALL sections from ALL pages to populate sectionQueues
      let allSections: string[] = [];
      try {
        allSections = Object.values(state.questionnaire.config!.pages).flatMap(
          (page) => page.sections,
        );
      } catch (_e) {
        //do nothing
      }

      const carried = state.questionnaire.queue
        .filter((extId) => !covered.has(extId) && state.kb.questions[extId])
        .map((extId) => state.kb.questions[extId]);

      // Sort questions for each section and store as question IDs
      // Process ALL sections, not just current page's sections
      const sortedSectionQueues = allSections.reduce<
        Record<string, ViewableQuestion['extId'][]>
      >((acc, section) => {
        const sectionBlocks = sortQuestions(
          [...payload, ...carried],
          state.questionnaire.queue,
          state.questionnaire.sectionAssignments,
          section,
        );
        // Extract question IDs from the sorted blocks
        acc[section] = sectionBlocks.flat().map((q) => q.extId);
        return acc;
      }, {});
      const emptiedSections = allSections.filter(
        (section) =>
          (state.questionnaire.sectionQueues[section] || []).length > 0 &&
          (sortedSectionQueues[section] || []).length === 0,
      );

      // Calculate activeQueue from the OLD sectionQueues (before update) to preserve current position
      const oldActiveQueue = currentPageSections.flatMap(
        (section) => state.questionnaire.sectionQueues[section] || [],
      );

      // Save the current question ID before rebuilding activeQueue
      const oldDisplayQueue = filterOnboardingQuestions(
        oldActiveQueue,
        state.graph.currentPage,
      );
      const currentQuestionId =
        oldDisplayQueue[state.questionnaire.currentQuestionIndex];

      // Store the section queues in state (primary storage, not just debugging)
      state.questionnaire.sectionQueues = sortedSectionQueues;

      state.questionnaire.isQueueReady = true;

      // Calculate the new activeQueue from updated sectionQueues
      const newActiveQueue = currentPageSections.flatMap(
        (section) => sortedSectionQueues[section] || [],
      );

      const carriedIds = new Set(carried.map((q) => q.extId));
      const newDisplayQueue = filterOnboardingQuestions(
        newActiveQueue,
        state.graph.currentPage,
      );
      const displayQueueWithoutCarried = filterOnboardingQuestions(
        newActiveQueue.filter((extId) => !carriedIds.has(extId)),
        state.graph.currentPage,
      );

      // DEBUG partial waves: Log when a wave carries questions or empties a section
      if (carried.length > 0 || emptiedSections.length > 0) {
        Sentry.addBreadcrumb({
          category: 'queue',
          message: 'activeQueue rebuilt by updateQuestions',
          level: 'info',
          data: {
            oldActiveQueueLength: oldDisplayQueue.length,
            newActiveQueueLength: newActiveQueue.length,
            payloadQuestionCount: payload.length,
            payloadQuestionIds: payload.slice(0, 20).map((q) => q.extId),
            carriedCount: carried.length,
            carriedQuestionIds: carried.slice(0, 20).map((q) => q.extId),
            coveredCount: coveredIds.length,
            queueLength: state.questionnaire.queue.length,
            emptiedSectionCount: emptiedSections.length,
          },
        });
      }

      if (
        carried.length > 0 &&
        displayQueueWithoutCarried.length === 0 &&
        newDisplayQueue.length > 0
      ) {
        Sentry.captureMessage(
          'QUESTIONNAIRE PARTIAL WAVE CARRIED QUESTIONS TO PRESERVE PAGE',
          {
            level: 'error',
            extra: {
              payloadQuestionCount: payload.length,
              carriedCount: carried.length,
              carriedQuestionIds: carried.slice(0, 20).map((q) => q.extId),
              coveredCount: coveredIds.length,
              queueLength: state.questionnaire.queue.length,
            },
          },
        );
      }

      // Adjust currentQuestionIndex if the question's position changed in the new activeQueue
      const newIndex = newDisplayQueue.indexOf(currentQuestionId);

      if (currentQuestionId && newIndex !== -1) {
        if (newIndex !== state.questionnaire.currentQuestionIndex) {
          Sentry.addBreadcrumb({
            category: 'queue',
            message: `QUESTIONS INSERTED BEFORE - Des questions ont été insérées avant la question ${currentQuestionId}. KB Version: ${state.version.current}`,
            level: 'info',
          });
        } else {
          const oldQuestionsBeforeCurrent = oldDisplayQueue.slice(
            0,
            state.questionnaire.currentQuestionIndex,
          );
          const newQuestionsBeforeCurrent = newDisplayQueue.slice(0, newIndex);
          const questionOrderChanged = oldQuestionsBeforeCurrent.some(
            (questionId, index) =>
              questionId !== newQuestionsBeforeCurrent[index],
          );

          if (questionOrderChanged) {
            Sentry.addBreadcrumb({
              category: 'queue',
              message: `QUESTIONS REORDERED - L'ordre des questions avant ${currentQuestionId} a changé. KB Version: ${state.version.current}`,
              level: 'info',
            });
          }
        }
        state.questionnaire.currentQuestionIndex = newIndex;
      }

      state.questionsUpdatedCount++;
      state.questionnaire.areQuestionsUpdating = false;
    })
    .addCase(updateFormulas, (state, action) => {
      state.kb.formulas = action.payload.reduce<RootState['kb']['formulas']>(
        (acc, formula) => {
          acc[formula.name] = formula.value;
          return acc;
        },
        {},
      );
      state.kb.formulasLoaded = true;
    })
    .addCase(updatePatient, (state, action) => {
      state.consultation.patient = action.payload;
    })
    .addCase(clearPatient, (state) => {
      delete state.consultation.patient;
    })
    .addCase(updatePractice, (state, action) => {
      state.consultation.practice = action.payload;
    })
    .addCase(clearPractice, (state) => {
      state.consultation.practice = undefined;
      state.consultation.professional = undefined;
      state.graph.personalizationFlags = {};
    })
    .addCase(updateReferrer, (state, action) => {
      state.referrer = action.payload;
      if (modes.includes(action.payload as Mode))
        state.graph.mode = action.payload as Mode;
    })
    .addCase(updateQuestionnaireId, (state, action) => {
      state.questionnaireId = action.payload;
    })
    .addCase(updateQuestionnaireRevision, (state, action) => {
      state.questionnaireRevision = action.payload;
    })
    .addCase(setQuestionnaireConflict, (state) => {
      state.questionnaireConflict = true;
    })
    .addCase(updateInvitationId, (state, action) => {
      state.invitationId = action.payload ?? undefined;
    })
    .addCase(updateInvitationSlug, (state, action) => {
      state.invitationSlug = action.payload ?? undefined;
    })
    .addCase(updateConsultationDate, (state, action) => {
      state.consultation.date = action.payload;
      state.graph.options.CONSULTATION_TIME_KNOWN = true;
    })
    .addCase(updateConsultationVariant, (state, action) => {
      state.consultation.variant = action.payload;
    })
    .addCase(setNoAppointmentMode, (state, action) => {
      if (action.payload) {
        state.consultation.noAppointment = true;
        state.graph.options.NO_APPOINTMENT = true;
      } else {
        state.consultation.noAppointment = false;
        state.graph.options.NO_APPOINTMENT = false;
      }
      // Re-resolve the effective professional so personalization picks up the
      // practice default when no professional is associated.
      const effectiveProfessional = getEffectiveProfessional(
        state.consultation,
      );
      state.graph.options.PATIENT_DOCUMENTS_ENABLED =
        effectiveProfessional?.patientDocumentsEnabled ?? false;
      state.graph.personalizationFlags =
        effectiveProfessional?.questionnaireOptions?.[state.graph.variant] ??
        getDefaultConfig(state.graph.variant);
    })
    .addCase(clearProfessional, (state) => {
      state.consultation.professional = undefined;
    })
    .addCase(updateProfessional, (state, action) => {
      state.consultation.professional = action.payload;
      state.consultation.practice = action.payload.practice;
      const effectiveProfessional = getEffectiveProfessional(
        state.consultation,
      );
      state.graph.options.PATIENT_DOCUMENTS_ENABLED =
        effectiveProfessional?.patientDocumentsEnabled ?? false;
      state.graph.personalizationFlags =
        effectiveProfessional?.questionnaireOptions?.[state.graph.variant] ??
        getDefaultConfig(state.graph.variant);
      if (effectiveProfessional?.gender) {
        const gender =
          effectiveProfessional.gender === 'MALE'
            ? 'Masculin'
            : effectiveProfessional.gender === 'FEMALE'
            ? 'Féminin'
            : 'Autre';
        state.graph.assertions.push(
          `[Médecin: #z‹]-(02.a_genre)->[Genre: ${gender}]`,
        );
      }
    })
    .addCase(acceptCGU, (state) => {
      const now = Date.now(); // Date objects are not serializable in redux-persist
      state.CGUAccepted = now;
    })
    .addCase(recordResearchConsent, (state, action) => {
      const now = Date.now(); // Date objects are not serializable in redux-persist
      if (action.payload === true) {
        state.researchConsent = now;
      }
    })
    .addCase(setMode, (state, action) => {
      state.graph.mode = action.payload;
    })
    .addCase(setInvitationFallback, (state, action) => {
      state.invitationFallback = action.payload;
    })
    .addCase(setPatientSegment, (state, action) => {
      state.patientSegment = action.payload;
    })
    .addCase(setOnboardingFlow, (state, action) => {
      state.graph.onboardingFlow = action.payload;
    })
    .addCase(setCurrentStepIndex, (state, action) => {
      state.graph.currentStepIndex = action.payload;
    })
    .addCase(PURGE, (state) => {
      const purged: RootState = {
        ...initialState,
        sessionId: uuid(),
        ...(state.isFullReset
          ? {}
          : {
              referrer: state.referrer,
              version: state.version,
              previousQueryVariant: state.previousQueryVariant,
              patientSegment: state.patientSegment,
              consultation: {
                practice: state.consultation.practice,
                professional: state.consultation.professional,
                variant: state.consultation.variant,
              },
              graph: {
                ...initialState.graph,
                mode: state.graph.mode,
              },
              questionnaire: {
                ...initialState.questionnaire,
                config: state.questionnaire.config,
              },
              searchParams:
                state.searchParams.reset === 'true'
                  ? omit(state.searchParams, ['reset'])
                  : {},
              previousSearchParams: state.previousSearchParams,
            }),
      };
      // useQuestionnaireVariant can't tell this graph from a loaded one when
      // the variant slug is 'DEFAULT', so rebuild it from the kept config.
      if (purged.questionnaire.config) {
        applyQuestionnaireConfig(purged, purged.questionnaire.config);
      }
      return purged;
    })
    .addCase(addToTotalQueue, (state, action) => {
      const { currentPage, queue } = action.payload;

      const index = state.questionnaire.totalQueue.findIndex((item) =>
        // eslint-disable-next-line no-prototype-builtins
        item.hasOwnProperty(currentPage),
      );

      if (index !== -1) {
        state.questionnaire.totalQueue[index] = { [currentPage]: queue };
      } else {
        state.questionnaire.totalQueue.push({ [currentPage]: queue });
      }
    })
    .addCase(setConsultationTimeKnown, (state, action) => {
      state.graph.options.CONSULTATION_TIME_KNOWN = action.payload;
    })
    .addCase(setAssistedByPro, (state, action) => {
      state.graph.options.ASSISTED_BY_PRO = action.payload;
    })
    .addCase(setDisplayFullQueue, (state, action) => {
      state.graph.options.DISPLAY_FULL_QUEUE = action.payload;
    })
    .addCase(setSurveyRatingSent, (state, action) => {
      state.questionnaire.surveyRatingSent = action.payload;
    })
    .addCase(setIsFrequentFlyer, (state, action) => {
      state.isFrequentFlyer = action.payload;
    })
    .addCase(setInterlocuter, (state, action) => {
      state.questionnaire.interlocuter = action.payload;
    })
    .addCase(setQuestionnaireConfig, (state, action) => {
      applyQuestionnaireConfig(state, action.payload);
    })
    .addCase(setQueryVariant, (state, action) => {
      state.queryVariant = action.payload;
    })
    .addCase(setSearchParams, (state, action) => {
      state.searchParams = action.payload;
    })
    .addCase(setFullReset, (state, action) => {
      state.isFullReset = action.payload;
    })
    .addCase(resetAreQuestionsUpdating, (state) => {
      state.questionnaire.areQuestionsUpdating = false;
    })
    .addCase(setExperimentFlags, (state, action) => {
      state.graph.experimentFlags = action.payload;
    });
});
